import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { track } from "../lib/analytics";
import {
  InstagramLogo,
  HandHeart,
  GlobeStand,
  ChalkboardTeacher,
  Leaf,
  PawPrint,
  Buildings,
  Camera,
  CurrencyInr,
  TrendUp,
  Sparkle,
  ArrowRight,
  Compass,
  Megaphone,
  Confetti,
  Check,
  Lightning,
  Tag,
  Briefcase,
  CaretRight,
  Star,
} from "@phosphor-icons/react";

const INSTAGRAM_HANDLE = "wanderfullyflying";
const INSTAGRAM_URL = `https://instagram.com/${INSTAGRAM_HANDLE}`;

// ---------------- Volunteering ----------------
const VOLUNTEER_PLATFORMS = [
  {
    name: "Workaway",
    blurb: "World's largest gig board for travel volunteers. Hosts in 170+ countries cover food + bed in exchange for ~4-5 hrs/day.",
    fee: "₹4,400 / yr",
    site: "https://workaway.info",
  },
  {
    name: "Worldpackers",
    blurb: "Curated hostels, NGOs, eco-farms across SEA, LatAm, Europe. Strong Indian community + verified hosts.",
    fee: "₹4,200 / yr",
    site: "https://worldpackers.com",
  },
  {
    name: "WWOOF",
    blurb: "Willing Workers On Organic Farms — country-by-country memberships. Best for farm/eco stays in Japan, Thailand, NZ.",
    fee: "₹2,000–3,500 / country",
    site: "https://wwoof.net",
  },
  {
    name: "Volunteer World",
    blurb: "Aggregator that lists structured NGO programs. Heavier fees but proper vetting + insurance.",
    fee: "Per program",
    site: "https://volunteerworld.com",
  },
  {
    name: "AIESEC India",
    blurb: "Student-led non-profit. Global Volunteer + Global Talent programs — bookable directly from Indian universities.",
    fee: "₹15-25k / 6-week stint",
    site: "https://aiesec.in",
  },
  {
    name: "GoEco",
    blurb: "Wildlife / conservation focused. Big-cat reserves in South Africa, elephant sanctuaries in Thailand.",
    fee: "Per program",
    site: "https://goeco.org",
  },
];

const VOLUNTEER_ROLES = [
  {
    Icon: Megaphone,
    color: "#E0457B",
    title: "Run social media for hostels & NGOs",
    body: "Hostels in Bali, Pai, Lisbon, Da Nang are perpetually looking for someone to shoot reels, manage their Insta, write captions, run paid ads. 4-5 hours/day in exchange for a private dorm + 2 meals. Build your portfolio while you travel.",
    perks: ["Free stay 1-3 months", "Reel/portfolio content", "Direct hostel contacts"],
  },
  {
    Icon: ChalkboardTeacher,
    color: "#2A7FB8",
    title: "Teach English at small NGOs",
    body: "Cambodia, Vietnam, Nepal, Sri Lanka — small village NGOs welcome native-fluent English speakers. TEFL helps but isn't always required. Expect 3-4 hours of class, plus admin help.",
    perks: ["Free stay + meals", "Deep local immersion", "Optional stipend ($100-300/mo)"],
  },
  {
    Icon: Leaf,
    color: "#3DB68B",
    title: "Eco-farm / permaculture work",
    body: "WWOOF organic farms in Japan, Thailand, Sri Lanka, NZ. Wake up early, work on the land for 5 hours, eat home-cooked food, sleep in cabins. Reset your dopamine, learn farming.",
    perks: ["Zero spend on food/stay", "Skill: regen agri", "Off-the-grid lifestyle"],
  },
  {
    Icon: PawPrint,
    color: "#FF7E4D",
    title: "Animal & wildlife sanctuaries",
    body: "Elephant sanctuaries (Chiang Mai), turtle conservation (Bali, Sri Lanka), big-cat reserves (South Africa). Most are paid programs — but ethical, structured, often 2-4 weeks.",
    perks: ["Hands-on conservation", "Vet/biology skill stack", "Unique reels"],
  },
  {
    Icon: Buildings,
    color: "#7C6CE0",
    title: "NGO admin & program ops",
    body: "Health NGOs in Nepal, education NGOs in Cambodia, women-empowerment orgs in Vietnam — they need help with grant writing, donor reports, English correspondence, basic accounting.",
    perks: ["Remote-work skills", "Strong LinkedIn proof", "Letter of recommendation"],
  },
  {
    Icon: Camera,
    color: "#F4B942",
    title: "Photo / video for tourism boards",
    body: "Smaller provincial tourism boards (Vietnam, Indonesia, Philippines, Sri Lanka) hire creators for short on-trip campaigns. Apply via their email + a tight 60-sec portfolio reel.",
    perks: ["Paid + free trip", "B-roll for your own grid", "Press accreditation"],
  },
];

// ---------------- Earnings tiers ----------------
const TIERS = [
  {
    key: "nano",
    label: "Nano",
    range: "1K – 10K",
    color: "#3DB68B",
    perPost: "₹500 – ₹3,000",
    perMonth: "₹5K – ₹25K",
    engagementPct: "5 – 10%",
    headline: "Niche, hyper-engaged audience. Most brands test the waters here.",
    why: [
      "Brands love nano-creators because engagement is 3-4× higher than macro accounts.",
      "Most deals are barter — free hotel stays, restaurant meals, gadget gifting — not always cash.",
      "Tourism boards rarely engage at this tier yet, so focus on local brands + city tourism.",
    ],
    sources: [
      { Icon: Tag, label: "Brand barters (free products, hotel stays)", color: "#3DB68B" },
      { Icon: Briefcase, label: "Occasional ₹2-5k cash deals from local brands", color: "#2A7FB8" },
      { Icon: Lightning, label: "Affiliate links (Booking, Klook) – ₹1-3k/mo", color: "#F4B942" },
      { Icon: Star, label: "UGC creator marketplaces (Trell, ShareChat, Plixxo)", color: "#FF7E4D" },
    ],
    nextStep: "Hit 10K with consistent, niche-tight reels. Pick one country, one vibe, post 4×/week. Cross-post to YouTube Shorts for the algorithm.",
  },
  {
    key: "micro",
    label: "Micro",
    range: "10K – 100K",
    color: "#2A7FB8",
    perPost: "₹3,000 – ₹50,000",
    perMonth: "₹30K – ₹2L",
    engagementPct: "3 – 6%",
    headline: "Sweet spot for travel creators in India — most full-time creators live here.",
    why: [
      "Hotels, airlines (IndiGo, Vistara), and tourism boards begin paying for content.",
      "You can negotiate paid press trips: ~₹15-30k + flights + 4-5 night stays.",
      "Affiliate income compounds — Booking.com / TravelPayouts / Amazon links + UGC for brands like MakeMyTrip.",
    ],
    sources: [
      { Icon: Briefcase, label: "Paid brand campaigns (₹10-50k per reel)", color: "#2A7FB8" },
      { Icon: Buildings, label: "Hotel barter + paid stays (₹5-20k/night offset)", color: "#7C6CE0" },
      { Icon: Tag, label: "Affiliate (TravelPayouts, Amazon) – ₹10-40k/mo", color: "#3DB68B" },
      { Icon: Camera, label: "UGC for brands (₹5-20k per deliverable, no posting)", color: "#FF7E4D" },
      { Icon: Compass, label: "Tourism-board day-trip campaigns (Kerala, Goa, J&K, Sri Lanka)", color: "#E0457B" },
    ],
    nextStep: "Build a media kit. Pitch tourism boards directly. Add YouTube long-form (8-12 min vlogs) — that's where AdSense kicks in (₹150-400 CPM in India).",
  },
  {
    key: "macro",
    label: "Macro",
    range: "100K – 1M",
    color: "#FF7E4D",
    perPost: "₹50,000 – ₹5L",
    perMonth: "₹3L – ₹15L",
    engagementPct: "2 – 4%",
    headline: "Career creator. Agency representation kicks in, multi-month retainers become possible.",
    why: [
      "An agent (OPraahFx, Monk Entertainment, Big Bang Social) starts pitching you to brands and shaves the friction.",
      "Tourism boards (Switzerland, Dubai, Thailand, UAE) book multi-country campaigns: ₹3-10L + flights + family of 2 + 5★ stays.",
      "YouTube long-form pays ₹2-5L/mo from AdSense alone if you post 4-8 vlogs/month with 100k+ views each.",
    ],
    sources: [
      { Icon: Briefcase, label: "Multi-deliverable campaigns (₹1-5L per package)", color: "#FF7E4D" },
      { Icon: GlobeStand, label: "Tourism-board international trips (₹5-15L/campaign)", color: "#E0457B" },
      { Icon: Star, label: "YouTube AdSense + Super Chats (₹2-5L/mo)", color: "#7C6CE0" },
      { Icon: Sparkle, label: "Affiliate + own merch/courses (₹50k-2L/mo)", color: "#3DB68B" },
      { Icon: Confetti, label: "Stage appearances, podcast guesting, brand events", color: "#2A7FB8" },
    ],
    nextStep: "Diversify into a second platform (YouTube long-form or LinkedIn). Launch a digital product (preset pack, itinerary template, course) — that's where 1Cr/yr creators are minted.",
  },
  {
    key: "mega",
    label: "Mega",
    range: "1M+",
    color: "#E0457B",
    perPost: "₹5L – ₹50L+",
    perMonth: "₹15L – ₹1Cr+",
    engagementPct: "1 – 3%",
    headline: "Celebrity-level reach. Brand ambassadorships, OTT cameos, and TV appearances follow.",
    why: [
      "You're now a media property. Tourism boards offer 6-12 month ambassadorships with retainers.",
      "Brands like Vistara, Tata Capital, Cred, Air India book one-shot reels at ₹10-50L.",
      "Exclusivity clauses become real — you might earn more from 2-3 huge deals/year than from continuous posting.",
    ],
    sources: [
      { Icon: GlobeStand, label: "Tourism-board ambassadorships (₹50L-2Cr/year)", color: "#E0457B" },
      { Icon: Briefcase, label: "Tier-1 brand campaigns (₹10-50L per reel)", color: "#FF7E4D" },
      { Icon: TrendUp, label: "YouTube AdSense + memberships (₹5-20L/mo)", color: "#7C6CE0" },
      { Icon: Sparkle, label: "Own merch / courses / agency (₹50L-5Cr/yr)", color: "#3DB68B" },
      { Icon: Confetti, label: "Bollywood / OTT cameos + book deals + speaking gigs", color: "#2A7FB8" },
    ],
    nextStep: "Build a team (manager, editor, second shooter). Productise — most mega creators earn 60% of revenue from things they own, not collabs.",
  },
];

export default function Creator() {
  const [tierKey, setTierKey] = useState("micro");
  const tier = TIERS.find((t) => t.key === tierKey) || TIERS[1];

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F0]">
      <Seo
        title="Become a travel content creator from India · Flycher"
        description="A no-fluff playbook for Indian travel creators — volunteer overseas for free stay, monetize your following, and an INR earnings calculator across nano, micro, macro & mega tiers."
        path="/creator"
      />
      <Navbar />
      <main className="flex-1">
        {/* HERO */}
        <section className="max-w-5xl mx-auto w-full px-5 sm:px-8 pt-28 pb-12">
          {/* Top Instagram CTA — first thing users see */}
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="creator-instagram-top"
            onClick={() =>
              track("creator_instagram_top_clicked", { handle: INSTAGRAM_HANDLE })
            }
            className="group inline-flex items-center gap-2.5 sm:gap-3 pl-1.5 pr-4 py-1.5 rounded-full text-white shadow-md hover:shadow-xl hover:scale-[1.01] transition-all max-w-full"
            style={{
              background:
                "linear-gradient(90deg, #833AB4 0%, #E0457B 50%, #F77737 100%)",
            }}
          >
            <span className="w-8 h-8 rounded-full bg-white/20 backdrop-blur grid place-items-center flex-shrink-0">
              <InstagramLogo size={16} weight="fill" />
            </span>
            <span className="text-[11px] sm:text-xs leading-tight min-w-0">
              <span className="font-semibold">Don't miss a single earning hack —</span>{" "}
              <span className="text-white/90">
                follow{" "}
                <span className="underline decoration-2 underline-offset-2">
                  @{INSTAGRAM_HANDLE}
                </span>
              </span>
            </span>
            <ArrowRight
              size={12}
              weight="bold"
              className="flex-shrink-0 group-hover:translate-x-0.5 transition-transform"
            />
          </a>

          <span className="kicker block mt-5">— For aspiring travel creators</span>
          <h1 className="font-serif text-4xl sm:text-6xl mt-2 text-slate-900 leading-tight">
            Travel the world,
            <br />
            <span className="italic" style={{ color: "#E0457B" }}>
              get paid for it.
            </span>
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-base sm:text-lg">
            A no-fluff playbook for Indian creators — how to{" "}
            <strong>volunteer overseas</strong> for free stays, what you can{" "}
            <strong>actually earn</strong> at each follower tier, and where to learn the
            rest from someone who's done it.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="#earnings"
              data-testid="creator-jump-earnings"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md"
              style={{ background: "#E0457B" }}
            >
              <CurrencyInr size={16} weight="bold" />
              See what creators earn
              <ArrowRight size={12} weight="bold" />
            </a>
            <a
              href="#volunteer"
              data-testid="creator-jump-volunteer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:border-[#3DB68B] hover:text-[#3DB68B]"
            >
              <HandHeart size={16} weight="duotone" />
              Travel-for-free: volunteer
              <ArrowRight size={12} weight="bold" />
            </a>
          </div>
        </section>

        {/* VOLUNTEERING */}
        <section
          id="volunteer"
          data-testid="creator-volunteer"
          className="max-w-5xl mx-auto w-full px-5 sm:px-8 py-12"
        >
          <div className="flex items-center gap-2">
            <HandHeart size={20} weight="duotone" color="#3DB68B" />
            <span className="kicker">— Path 1 · Volunteer for free trips</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl mt-3 max-w-3xl text-slate-900">
            Free stays + content + a cause —{" "}
            <span className="italic" style={{ color: "#3DB68B" }}>
              all at once.
            </span>
          </h2>
          <p className="text-slate-600 mt-4 max-w-2xl leading-relaxed">
            Before you have a follower count to monetise, volunteering is the highest-ROI
            way to travel cheap (or free) while building portfolio content + cross-border
            credibility. Indian passport-holders are welcomed by most of these networks.
          </p>

          {/* Role cards */}
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {VOLUNTEER_ROLES.map((r, i) => {
              const { Icon } = r;
              return (
                <article
                  key={r.title}
                  data-testid={`volunteer-role-${i}`}
                  className="rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-11 h-11 rounded-xl grid place-items-center flex-shrink-0"
                      style={{ background: r.color + "1A", color: r.color }}
                    >
                      <Icon size={22} weight="duotone" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg text-slate-900 leading-snug">
                        {r.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{r.body}</p>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {r.perks.map((p) => (
                      <li
                        key={p}
                        className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full"
                        style={{
                          background: r.color + "12",
                          color: r.color,
                        }}
                      >
                        <Check size={11} weight="bold" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>

          {/* Platforms */}
          <div className="mt-10">
            <span className="kicker">— Trusted platforms to find gigs</span>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {VOLUNTEER_PLATFORMS.map((p) => (
                <a
                  key={p.name}
                  href={p.site}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  data-testid={`volunteer-platform-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => track("creator_platform_clicked", { platform: p.name })}
                  className="rounded-2xl bg-white border border-slate-200 p-4 hover:border-[#3DB68B] hover:shadow-md transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-lg text-slate-900">{p.name}</h3>
                    <CaretRight
                      size={16}
                      weight="bold"
                      className="text-slate-300 group-hover:text-[#3DB68B] group-hover:translate-x-1 transition-all"
                    />
                  </div>
                  <p className="text-xs text-slate-600 mt-2 leading-relaxed">{p.blurb}</p>
                  <p className="mt-3 text-[10px] uppercase tracking-wider font-mono text-slate-400">
                    Fee · <span className="text-slate-700">{p.fee}</span>
                  </p>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-8 rounded-2xl bg-[#EAF4FB] border border-[#2A7FB8]/15 p-5">
            <span className="kicker">— Pro tip</span>
            <p className="text-sm text-slate-800 mt-2 leading-relaxed">
              Apply <strong>3-4 weeks before</strong> your travel window — popular Bali /
              Pai / Lisbon hostels are booked solid. Lead your DM/email with a 30-sec
              reel of past content + 3 dates you can commit to. Responses 4× faster.
            </p>
          </div>
        </section>

        {/* EARNINGS CALCULATOR */}
        <section
          id="earnings"
          data-testid="creator-earnings"
          className="max-w-5xl mx-auto w-full px-5 sm:px-8 py-12"
        >
          <div className="flex items-center gap-2">
            <CurrencyInr size={20} weight="bold" color="#E0457B" />
            <span className="kicker">— Path 2 · Monetize your follower count</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl mt-3 max-w-3xl text-slate-900">
            What can you really earn at{" "}
            <span className="italic" style={{ color: "#E0457B" }}>
              your follower count?
            </span>
          </h2>
          <p className="text-slate-600 mt-4 max-w-2xl leading-relaxed">
            Pick where your Instagram (or YouTube) sits today. We'll break down what
            similar Indian travel creators are pulling in per post, per month, and{" "}
            <em>why</em>.
          </p>

          {/* Tier picker */}
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {TIERS.map((t) => {
              const active = tierKey === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`tier-${t.key}`}
                  onClick={() => {
                    setTierKey(t.key);
                    track("creator_tier_selected", { tier: t.key });
                  }}
                  className={`rounded-2xl p-4 text-left border transition ${
                    active
                      ? "text-white border-transparent shadow-lg"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  style={active ? { background: t.color } : {}}
                >
                  <p
                    className={`text-[10px] font-mono uppercase tracking-wider ${
                      active ? "text-white/85" : "text-slate-400"
                    }`}
                  >
                    {t.range}
                  </p>
                  <p className="font-serif text-2xl mt-0.5">{t.label}</p>
                  <p
                    className={`text-[11px] mt-1 ${
                      active ? "text-white/85" : "text-slate-500"
                    }`}
                  >
                    {t.engagementPct} engagement
                  </p>
                </button>
              );
            })}
          </div>

          {/* Tier detail card */}
          <article
            data-testid="tier-detail"
            className="mt-6 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden"
          >
            <div
              className="p-6 sm:p-8 text-white"
              style={{
                background: `linear-gradient(135deg, ${tier.color} 0%, ${tier.color}CC 100%)`,
              }}
            >
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/80">
                {tier.label} creator · {tier.range} followers
              </span>
              <p className="font-serif text-2xl sm:text-3xl mt-2 leading-snug max-w-2xl">
                {tier.headline}
              </p>

              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl bg-white/15 backdrop-blur p-4">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/85">
                    Per sponsored post
                  </p>
                  <p className="font-serif text-3xl sm:text-4xl mt-1 font-semibold">
                    {tier.perPost}
                  </p>
                </div>
                <div className="rounded-xl bg-white/15 backdrop-blur p-4">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/85">
                    Realistic monthly income
                  </p>
                  <p className="font-serif text-3xl sm:text-4xl mt-1 font-semibold">
                    {tier.perMonth}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 grid lg:grid-cols-2 gap-6">
              <div>
                <span className="kicker">— Why creators at this tier earn this</span>
                <ul className="mt-3 space-y-2.5">
                  {tier.why.map((w, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2 leading-relaxed">
                      <Lightning
                        size={14}
                        weight="fill"
                        className="mt-1 flex-shrink-0"
                        color={tier.color}
                      />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="kicker">— Where the money comes from</span>
                <ul className="mt-3 space-y-2">
                  {tier.sources.map((s, i) => {
                    const { Icon } = s;
                    return (
                      <li
                        key={i}
                        className="text-sm text-slate-700 flex items-start gap-2.5 rounded-xl border border-slate-100 px-3 py-2"
                      >
                        <Icon
                          size={16}
                          weight="duotone"
                          className="mt-0.5 flex-shrink-0"
                          color={s.color}
                        />
                        <span>{s.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="mx-6 sm:mx-8 mb-6 sm:mb-8 rounded-xl bg-[#FFF1E8] border border-[#FF7E4D]/15 p-4">
              <span className="kicker">— What to do next</span>
              <p className="text-sm text-slate-800 mt-1.5 leading-relaxed">{tier.nextStep}</p>
            </div>
          </article>

          <p className="text-[11px] text-slate-400 font-mono text-center mt-4">
            * Ranges aggregated from public creator interviews (2024-2026), Indian brand-rate
            cards, and direct contributor data. Cash deals exclude organic affiliate income.
          </p>
        </section>

        {/* INSTAGRAM CTA */}
        <section
          data-testid="creator-instagram-cta"
          className="max-w-5xl mx-auto w-full px-5 sm:px-8 py-12"
        >
          <div
            className="rounded-3xl p-7 sm:p-10 text-white shadow-2xl overflow-hidden relative"
            style={{
              background:
                "linear-gradient(135deg, #833AB4 0%, #E0457B 40%, #F77737 80%, #FCB045 100%)",
            }}
          >
            <div className="relative z-10 grid md:grid-cols-[1fr,auto] gap-6 items-center">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/90">
                  — Path 3 · Learn from someone living it
                </span>
                <h2 className="font-serif text-3xl sm:text-5xl mt-2 leading-tight">
                  Want the full creator playbook?
                </h2>
                <p className="mt-3 text-base sm:text-lg text-white/95 max-w-xl leading-relaxed">
                  We share weekly insights on landing brand deals, pitching tourism boards,
                  pricing your reels, and how Indian creators are actually getting paid —
                  all on Instagram.
                </p>
                <p className="mt-2 text-sm text-white/85">
                  Saved Insta highlights cover: media kit templates, cold-pitch DMs that
                  work, and the exact rate cards we recommend.
                </p>
              </div>
              <div className="flex flex-col items-stretch gap-3 md:min-w-[260px]">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="creator-instagram-follow"
                  onClick={() =>
                    track("creator_instagram_clicked", { handle: INSTAGRAM_HANDLE })
                  }
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl text-base font-bold text-slate-900 bg-white shadow-xl hover:scale-[1.02] transition-transform"
                >
                  <InstagramLogo size={22} weight="fill" />
                  Follow @{INSTAGRAM_HANDLE}
                </a>
                <p className="text-[11px] text-white/85 text-center font-mono uppercase tracking-wider">
                  Free · New reel every week
                </p>
              </div>
            </div>
            {/* Decorative blobs */}
            <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-10 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          </div>
        </section>

        {/* PLAN CTA */}
        <section className="max-w-5xl mx-auto w-full px-5 sm:px-8 pb-20">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
            <div>
              <span className="kicker">— While you build your following</span>
              <h3 className="font-serif text-2xl sm:text-3xl mt-2 text-slate-900">
                Use Flycher to actually <em>do</em> the trips.
              </h3>
              <p className="text-sm text-slate-600 mt-1.5 max-w-xl">
                AI itineraries, real flight prices, scam-check before you go — designed
                for Indian travelers building their first travel-creator brand.
              </p>
            </div>
            <Link
              to="/itinerary"
              data-testid="creator-plan-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md flex-shrink-0"
              style={{ background: "#FF7E4D" }}
            >
              <Sparkle size={14} weight="fill" />
              Plan my next trip
              <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
